/**
 * DEC3 on the page (spec §4.3): which definition a Result tile counts.
 * - Reach in force: the override badge (box 9's Awareness row, or the whole
 *   box 3 card) and the reach caveat, everywhere Reach shows.
 * - A failed read: "โหลดนิยาม Result ไม่ได้" — the cells are already "—".
 * - All workspaces / the builder preview: "ใช้นิยามกลาง".
 * Reads only tile.resultMetric (the server's word); never re-derives it.
 * The Awareness row is decided by objectiveOfRow, the one rule (DEC3 F8).
 *
 * @module components/looker/ResultMetricNotes
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LookerRow, LookerTile } from '@/services/looker/lookerTypes';
import { objectiveOfRow } from '@/services/looker/lookerTargetCells';

/** The override chip beside box 9's Awareness row. */
export const ResultBadge: React.FC<{ tile: LookerTile; row: LookerRow }> = ({ tile, row }) => {
  const { t } = useTranslation();
  const m = tile.resultMetric;
  if (!m || !m.overridden || m.awarenessAt !== 'rows' || objectiveOfRow(tile, row) !== 'awareness') return null;
  return (
    <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-ink-900 px-1.5 text-[11px] text-ink-900">
      {t('looker.resultMapping.badge')}
    </span>
  );
};

/** Card lines: the box 3 badge, the reach caveat, a failed read, or the central definition. */
export const ResultMetricLines: React.FC<{ tile: LookerTile }> = ({ tile }) => {
  const { t } = useTranslation();
  const m = tile.resultMetric;
  if (!m || tile.error) return null;
  if (m.failed) return <p className="mt-2 text-xs font-medium text-error-700">{t('looker.resultMapping.failed')}</p>;
  if (m.overridden && m.awareness === 'reach' && m.awarenessAt !== null) {
    return (
      <>
        {m.awarenessAt === 'card' && <p className="mt-2 text-xs font-medium text-ink-900">{t('looker.resultMapping.badge')}</p>}
        <p className="mt-2 text-xs text-warning-700">{t('looker.resultMapping.caveat')}</p>
      </>
    );
  }
  return m.central ? <p className="mt-2 text-xs text-neutral-700">{t('looker.resultMapping.central')}</p> : null;
};
